/**
 * Pending assessment request (sv-3n86)
 *
 * A new CTO invited by an investor signs up from a link carrying the request
 * token (and their email). SignUpPage stashes both; any authenticated landing
 * page redirects to the request while it is stashed; AssessmentRequestAcceptPage
 * consumes it on mount. Only the request page consumes it — consuming earlier
 * lets a later redirect (e.g. Clerk's post-task redirect) strand the CTO on
 * /assessments.
 *
 * The stash is bound to the invited email: on a shared browser, a different
 * account is never sent to someone else's request, and the stale stash is
 * discarded (CodeRabbit, PR #410).
 *
 * Storage can be unavailable (private mode, blocked site data); every accessor
 * degrades to "no pending request" rather than throwing.
 */

export const PENDING_ASSESSMENT_REQUEST_KEY =
  "stackverify_pending_assessment_request";
const PENDING_EMAIL_KEY = `${PENDING_ASSESSMENT_REQUEST_KEY}_email`;

function normalizeEmail(email: string | null | undefined): string {
  return (email ?? "").trim().toLowerCase();
}

/**
 * Stash the request token from the invite link, bound to the invited email.
 * @param token - The raw assessment-request token from the sign-up URL.
 * @param invitedEmail - The invited CTO's email from the sign-up URL, if present.
 * @returns Nothing.
 * @throws Never — storage failures are swallowed.
 */
export function stashPendingAssessmentRequest(
  token: string,
  invitedEmail?: string | null,
): void {
  try {
    localStorage.setItem(PENDING_ASSESSMENT_REQUEST_KEY, token);
    const email = normalizeEmail(invitedEmail);
    if (email) {
      localStorage.setItem(PENDING_EMAIL_KEY, email);
    } else {
      localStorage.removeItem(PENDING_EMAIL_KEY);
    }
  } catch {
    // Storage unavailable: the CTO can still open the request from the email.
  }
}

/**
 * Read the stashed token without consuming it.
 * @returns The stashed token, or null when none is stashed or storage is unavailable.
 * @throws Never — storage failures are swallowed.
 */
export function getPendingAssessmentRequest(): string | null {
  try {
    return localStorage.getItem(PENDING_ASSESSMENT_REQUEST_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Consume the stashed request. Called only by the request page.
 * @param token - When given, clear only if the stash is for this request, so
 *   opening a different request does not discard the one the user signed up from.
 * @returns Nothing.
 * @throws Never — storage failures are swallowed.
 */
export function clearPendingAssessmentRequest(token?: string): void {
  try {
    if (token !== undefined && getPendingAssessmentRequest() !== token) return;
    localStorage.removeItem(PENDING_ASSESSMENT_REQUEST_KEY);
    localStorage.removeItem(PENDING_EMAIL_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/**
 * Path of the stashed request, if it belongs to the signed-in account.
 * A stash bound to a different email is discarded; while the signed-in email is
 * unknown, a bound stash is withheld but kept.
 * @param currentEmail - The signed-in user's email.
 * @returns `/assessment-requests/{token}` when a matching token is stashed, else null.
 * @throws Never.
 */
export function pendingAssessmentRequestPath(
  currentEmail?: string | null,
): string | null {
  const token = getPendingAssessmentRequest();
  if (!token) return null;

  let boundEmail = "";
  try {
    boundEmail = normalizeEmail(localStorage.getItem(PENDING_EMAIL_KEY));
  } catch {
    return null;
  }

  if (boundEmail) {
    const current = normalizeEmail(currentEmail);
    if (!current) return null;
    if (current !== boundEmail) {
      clearPendingAssessmentRequest();
      return null;
    }
  }
  return `/assessment-requests/${encodeURIComponent(token)}`;
}
